<template>
  <div class="transaction">
    <img
      class="t-image"
      :src="icon"
      alt="cryptoactive"
      @click="toToken"
    >

    <div class="transaction-info">
      <div class="transaction-info__item transaction-info__left">
        <div class="eth-adress">
          <UBlockscanAddress
            class="transaction-info__item-name a_link"
            :volume="addressEth"
            :name="name"
          />
        </div>

        <div class="fs11 transaction-info__type">
          {{
            isInvestmentHistory
              ? prepareInvestmentHistoryNotes
              : prepareTransactionNotes
          }}
          <span v-if="!isInvestmentHistory">
            <span v-if="projectId && showProjectLink">
              <RouterLink
                :to="`/project-card/${projectId}`"
                class="link"
                target="_blank"
              >
                {{ projectName }}
              </RouterLink>
            </span>
            <span
              v-if="!isConfirmed"
              class="pending"
            >[pending]</span>
            <span v-if="projectId && prepareTransactionNotes == 'Distribution'">for
              <RouterLink
                :to="`/project-card/${projectId}`"
                class="link"
                target="_blank"
              >
                {{ projectName }}
              </RouterLink>
            </span>
          </span>
        </div>
        <div
          v-if="
            prepareTransactionNotes == $t('transactions.type.investing') &&
              showTypeTrx
          "
          class="type-info"
        >
          Synced with Hyperledger Fabric
        </div>
      </div>

      <div class="transaction-info__item transaction-info__right">
        <div
          :single="!isOwnerTransaction"
          class="transaction-info__item-inner"
        >
          <div
            v-if="isOwnerTransaction"
            class="transaction-info__dot"
          >
            <UTooltipInfo
              :type="'info'"
              :text="'It\'s yours transaction!'"
            />
          </div>
          <div class="transaction-info__right">
            <div class="fs14 transaction-info__amount">
              <span class="sign">
                {{ sign }}
              </span>
              <UDecimal
                :value="amount"
                :decimals="decimals"
                :indent="indent"
                :addition="mnemonic"
                :smart-indent="true"
              />
            </div>

            <div class="fs11 transaction-info__card-type">
              {{ cardType }}
            </div>
          </div>
        </div>

        <UBlockscanHash
          class="transaction-info__item-name a_link"
          :volume="hash"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import _ from "lodash";
import UBlockscanAddress from "@/components/uikit/uComponents/UBlockscanAddress.vue";
import UBlockscanHash from "@/components/uikit/uComponents/UBlockscanHash.vue";
import UTooltipInfo from "@/components/uikit/uComponents/UTooltipInfo.vue";
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";

import i18n from "@/localization/localization";

const { t } = i18n.global;

const props = defineProps({
  addressEth: {
    type: [String, null],
    default: "",
    required: true
  },
  addressFrom: {
    type: String,
    default: ""
  },
  addressTo: {
    type: String,
    default: ""
  },
  amount: {
    type: [Number, String],
    required: true
  },
  cardType: {
    type: String,
    required: true
  },
  decimals: {
    type: Number,
    default: 0
  },
  hash: {
    type: String,
    required: true
  },
  icon: {
    type: String,
    default: "/svg/companies/s_logo.svg"
  },
  indent: {
    type: Number,
    default: 2
  },
  invesrtSigns: {
    type: Boolean,
    default: false
  },
  isConfirmed: {
    type: Boolean,
    default: true
  },
  isInvestmentHistory: {
    type: Boolean,
    default: false
  },
  isOwnerTransaction: {
    type: Boolean,
    default: false
  },
  isRefill: {
    type: Boolean,
    default: true
  },
  isVoting: {
    type: Boolean,
    default: false
  },
  mnemonic: {
    type: String,
    default: ""
  },
  name: {
    type: String,
    required: true
  },
  projectId: {
    type: Number,
    default: null
  },
  projectName: {
    type: String,
    default: ""
  },
  transactionType: {
    type: String,
    required: true
  }
});

const router = useRouter();
const store = useStore();

const tokens = computed(() => store.getters.tokens);

const showTypeTrx = computed(
  () => process.env.VUE_APP_HYPERLEDGER_FABRIC === "true"
);

const prepareTransactionNotes = computed(() => {
  switch (props.transactionType) {
    case "transfer":
      return t("transactions.type.transfer");
    case "deposit":
      return t("transactions.type.depos");
    case "dividends":
      return t("transactions.type.dividends");
    case "investing_nft":
      return t("transactions.type.investingNft");
    case "investing":
      return props.isVoting
        ? t("transactions.type.investingVote")
        : t("transactions.type.allocation");
    case "capital_payment":
      return t("transactions.type.payment");
    case "capital_refund":
      return props.isVoting
        ? t("transactions.type.refundVote")
        : t("transactions.type.refund");
    case "vote":
      return t("transactions.type.investingVote");
    case "hold for withdrawal":
      return t("transactions.type.holdWithdraw");
    case "vote_refund":
      return t("transactions.type.voteRefund");
    case "mint":
      return t("transactions.type.mint");
    case "mint_hold":
      return t("transactions.type.mintHold");
    case "vote_dividents":
      return t("transactions.type.voteDividents");
    case "investments_release":
      return t("transactions.type.tokensRelease");
    case "conversation_receive":
      return t("transactions.type.exchange");
    case "conversation_send":
      return t("transactions.type.exchange");
    case "conversation_commission":
      return t("transactions.type.commission");
    case "staking_withdraw":
      return t("transactions.type.stakingWithdraw");
    case "Transfer to the exchange":
      return t("transactions.type.transferToExchange");
    case "Transfer from the exchange":
      return t("transactions.type.transferFromExchange");
    case "referrals":
      return t("transactions.type.referrals");
    default:
      return props.transactionType || t("transactions.unspecified");
  }
});

const prepareInvestmentHistoryNotes = computed(() => {
  switch (props.transactionType) {
    case "transfer":
      return t("transactions.typeInvestment.transfer");
    case "deposit":
      return t("transactions.typeInvestment.depos");
    case "dividends":
      return t("transactions.typeInvestment.dividends");
    case "investing_nft":
      return t("transactions.typeInvestment.investingNft");
    case "investing":
      return props.isVoting
        ? t("transactions.typeInvestment.investingVote")
        : t("transactions.typeInvestment.allocation");
    case "capital_payment":
      return t("transactions.typeInvestment.payment");
    case "capital_refund":
      return props.isVoting
        ? t("transactions.typeInvestment.refundVote")
        : t("transactions.typeInvestment.refund");
    case "vote":
      return t("transactions.typeInvestment.investingVote");
    case "hold for withdrawal":
      return t("transactions.typeInvestment.holdWithdraw");
    case "vote_refund":
      return t("transactions.typeInvestment.voteRefund");
    case "mint":
      return t("transactions.typeInvestment.mint");
    case "mint_hold":
      return t("transactions.typeInvestment.mintHold");
    case "vote_dividents":
      return t("transactions.typeInvestment.voteDividents");
    case "investments_release":
      return t("transactions.typeInvestment.tokensRelease");
    case "conversation_receive":
      return t("transactions.typeInvestment.exchange");
    case "conversation_send":
      return t("transactions.typeInvestment.exchange");
    case "conversation_commission":
      return t("transactions.typeInvestment.commission");
    case "staking_withdraw":
      return t("transactions.typeInvestment.stakingWithdraw");
    case "Transfer to the exchange":
      return t("transactions.typeInvestment.transferToExchange");
    case "Transfer from the exchange":
      return t("transactions.typeInvestment.transferFromExchange");
    case "referrals":
      return t("transactions.type.referrals");
    default:
      return props.transactionType || t("transactions.unspecified");
  }
});

const sign = computed(() => {
  if (props.invesrtSigns) {
    return props.isRefill ? "-" : "+";
  }
  return props.isRefill ? "+" : "-";
});

const tokenId = computed(
  () => _.find(tokens.value, { mnemonicEng: props.mnemonic })?.id
);

const showProjectLink = computed(() => {
  const notes = prepareTransactionNotes.value;

  return (
    notes === t("transactions.type.allocation") ||
    notes === t("transactions.type.refund") ||
    notes === t("transactions.type.investingVote") ||
    notes === t("transactions.type.investingNft") ||
    notes === t("transactions.type.mint") ||
    notes === t("transactions.type.mintHold") ||
    notes === t("transactions.type.voteDividents") ||
    notes === t("transactions.type.stakingWithdraw") ||
    notes === t("transactions.type.tokensRelease") ||
    notes === t("transactions.type.refundVote")
  );
});

const toToken = () => {
  router.push({ name: "token info", params: { id: tokenId.value } });
};
</script>

<style lang="scss">
.transaction-info__right {
  .decimal-static {
    .value {
      @media screen and (max-width: 490px) {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 100px;
      }

      @media screen and (max-width: 400px) {
        max-width: 70px;
      }
    }
  }
}
</style>

<style scoped lang="scss">
.a_link {
  font-size: 14px;
  color: var(--text-color);
}
.link {
  color: var(--text-color);
  text-decoration: underline;
  margin-right: 4px;
}

.t-image {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  object-fit: cover;
  &:hover {
    cursor: pointer;
  }
}
.transaction {
  display: flex;
  align-items: center;

  &-info {
    display: flex;
    justify-content: space-between;
    width: 100%;
    height: 100%;

    margin-left: 8px;

    &__type {
      color: var(--color-60);
    }

    &__card-type {
      color: var(--color-60);
      text-align: right;
    }

    .pending {
      color: red;
    }

    &__amount[isRefill="true"] {
      color: #5ac396;
    }

    &__amount[isRefill="false"] {
      color: #e94545;
    }

    &__item {
      display: flex;
      flex-direction: column;

      &-name {
        text-decoration: underline;
      }

      &-inner {
        display: flex;
        justify-content: space-between;
        align-items: center;
        min-width: 120px;
        gap: 10px;

        @media screen and (max-width: 400px) {
          align-items: baseline;
          min-width: 112px;
          gap: 5px;
        }

        &[single="true"] {
          justify-content: end;
        }
      }
    }

    &__left {
      align-items: flex-start;
    }

    &__right {
      justify-content: end;
      align-items: center;
      flex-direction: row;
      white-space: nowrap;
      text-align: end;
    }
  }
}
.uppercase::first-letter {
  text-transform: uppercase;
}

.dot {
  width: 10px;
  height: 10px;
  background: #5fc396;
  border-radius: 50%;
}
.sign {
  margin-right: 2px;
}
.eth-adress {
  display: flex;
  align-items: center;
}
.type-info {
  font-size: 9px;
  margin-top: -1px;
  color: var(--color-50);
}
@media screen and (max-width: 580px) {
  .eth-adress {
    flex-direction: column;
    align-items: start;
  }
}
</style>
